import React from 'react';
import cx from 'classnames';

import { TABLET_MIN, DESKTOP_MIN } from 'libs/universal/utils/device';
import Head from 'next/head';
import styles from './HeroV2.module.less';

type HeroV2Props = {
  height?: string;
  backgroundImage?: string;
  bgImgExtension?: 'jpg' | 'jpeg' | 'JPG' | 'JPEG' | 'png' | 'PNG';
  mobileBackgroundImage?: string;
  tabletBackgroundImage?: string;
  className?: string;
  backgroundChips?: string;
  buttons?: JSX.Element;
  secondaryContents?: JSX.Element;
  title?: JSX.Element | string;
  caption?: JSX.Element;
  body?: JSX.Element;
  secondaryRight?: boolean;
  headerTag?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  component?: 'div' | 'section';
  alignCenterOnMobile?: boolean;
  borderBottomOnMobile?: boolean;
  imageDirectory?: string;
  notVersioned?: boolean;
};

const HeroV2: React.FC<HeroV2Props> = ({
  backgroundImage,
  bgImgExtension = 'jpg',
  className = '',
  buttons,
  title,
  headerTag: HeaderTag = 'h1' as keyof JSX.IntrinsicElements,
  caption = undefined,
  body,
  backgroundChips = undefined,
  height = undefined,
  borderBottomOnMobile = false,
  imageDirectory,
  notVersioned = false,
  children,
}): JSX.Element => {
  const backgroundStyle = { height };
  const versionSuffix = notVersioned ? '' : '@2x';
  const renderPrimarySection = () => {
    return (
      <>
        <Head>
          <link
            rel="preload"
            href={`${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_mobile.${bgImgExtension}`}
            as="image"
            media="(max-width: 480px)"
          />
        </Head>
        <div className="hero__DesktopContents">
          {title && HeaderTag && <HeaderTag className={styles.title}>{title}</HeaderTag>}
          {caption && <div className={styles.caption}>{caption}</div>}
          <div className="hero__body">
            {body && <div>{body}</div>}
            {buttons && <div className={styles.buttons}>{buttons}</div>}
          </div>
        </div>
      </>
    );
  };

  return (
    <section
      className={cx(styles.hero, { [styles.borderBottom]: borderBottomOnMobile }, className)}
      style={backgroundStyle}
    >
      <div className={cx(styles.blockImage, 'hero__notDesktop')} style={backgroundStyle} />
      {backgroundChips}
      <div className={styles.contents}>{renderPrimarySection()}</div>
      {children}
      {/* prettier-ignore */}
      <style jsx>{`
        @media only screen {
          /* Small screen, non-retina */
          .${styles.blockImage} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_mobile.${bgImgExtension});
          }
        }

        @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: 320px),
          only screen and (min-resolution: 200dpi) and (min-width: 320px), {
          /* Small screen, retina, stuff to override above media query */
          .${styles.blockImage} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_mobile${versionSuffix}.${bgImgExtension});
          }
        }

        @media only screen and (min-width: ${TABLET_MIN}px) and (max-width: ${DESKTOP_MIN}px) {
          /* Medium screen, non-retina */
          .${styles.blockImage} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_tablet.${bgImgExtension});
          }
        }

        @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: ${TABLET_MIN}px) and (max-width: ${DESKTOP_MIN}px),
          only screen and (min-resolution: 200dpi) and (min-width: ${TABLET_MIN}px), {
          /* Medium screen, retina, stuff to override above media query */
          .${styles.blockImage} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_tablet${versionSuffix}.${bgImgExtension});
          }
        }

        @media only screen and (min-width: ${DESKTOP_MIN}px) {
          /* Large screen, non-retina */
          .${styles.hero} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_desktop.${bgImgExtension});
          }
        }

        @media only screen and (-webkit-min-device-pixel-ratio: 2) and (min-width: ${DESKTOP_MIN}px),
          only screen and (min-resolution: 200dpi) and (min-width: ${DESKTOP_MIN}px), {
          /* Large screen, retina, stuff to override above media query */
          .${styles.hero} {
            background-image: url(${process.env.ASSET_PATH}/static-preauth/images/${imageDirectory}/${backgroundImage}_desktop${versionSuffix}.${bgImgExtension});
          }
        }
      `}</style>
    </section>
  );
};

export default HeroV2;
